import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import { useCallback } from 'react';
import { useUpdate } from 'react-admin';

import { Semaphore } from '@/screens/therapistAppConfig/Semaphore';

import {
  computeEffectiveBoolean,
  computeEffectiveEnum,
} from './computeEffectiveFlag';
import { MatureChip } from './MatureChip';
import {
  BooleanFlagEntry,
  EnumFlagEntry,
  FlagEntry,
  FlagRecord,
} from './types';

type CommonProps = {
  therapistId: string;
  therapistRecord: FlagRecord;
  globalConfig: FlagRecord;
  onAfterChange?: () => void;
};

const lockedTooltip =
  'Locked: this flag is enabled globally, so per-therapist cannot turn it off. Change the global App Config to disable for everyone.';

const Header = ({
  label,
  description,
  mature,
}: {
  label: string;
  description?: string;
  mature?: boolean;
}) => (
  <Box
    display="flex"
    alignItems="center"
    gap={0.5}
    marginBottom={0.5}
    flexWrap="wrap"
  >
    <Typography variant="subtitle2" fontWeight={600}>
      {label}
    </Typography>
    {description ? (
      <Tooltip title={description}>
        <IconButton size="small" sx={{ padding: 0.25 }}>
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null}
    {mature ? <MatureChip /> : null}
  </Box>
);

const GlobalBooleanIndicator = ({ global }: { global: boolean }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={0.5}
    sx={{ color: 'text.secondary' }}
  >
    <PublicIcon sx={{ fontSize: '0.95rem' }} />
    <Semaphore
      enabled={global}
      enabledText="Global: on"
      disabledText="Global: off"
    />
  </Box>
);

const GlobalEnumIndicator = ({ value }: { value: string }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={0.5}
    sx={{ color: 'text.secondary' }}
  >
    <PublicIcon sx={{ fontSize: '0.95rem' }} />
    <Typography variant="caption">Global: {value}</Typography>
  </Box>
);

const BooleanTherapistRow = ({
  entry,
  therapistId,
  therapistRecord,
  globalConfig,
  onAfterChange,
}: CommonProps & { entry: BooleanFlagEntry }) => {
  const [updateTherapist, { isLoading }] = useUpdate('therapists', {
    id: therapistId,
  });

  const { global, therapist, effective, locked } =
    computeEffectiveBoolean(entry, globalConfig, therapistRecord);

  const mature = entry.stability === 'mature';
  /** Global OR-merge is on: effective is forced on; hide switch (same layout idea as mature, no maturity chip). */
  const globalOnForced = !mature && locked && global;
  const disabled = locked || isLoading;

  const onToggle = useCallback(async () => {
    if (disabled || !entry.therapistField) return;
    await updateTherapist(
      'therapists',
      {
        id: therapistId,
        data: {
          [entry.therapistField]: therapist ? 0 : 1,
          lastUpdatedAt: new Date(),
        },
      },
      {
        onSuccess: () => {
          onAfterChange?.();
        },
      },
    );
  }, [
    disabled,
    entry.therapistField,
    onAfterChange,
    therapist,
    therapistId,
    updateTherapist,
  ]);

  return (
    <Box
      sx={{
        py: 1.25,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Header
        label={entry.label}
        description={entry.description}
        mature={mature}
      />

      {!mature && globalOnForced ? (
        <Tooltip title={lockedTooltip}>
          <Box display="inline-flex">
            <GlobalBooleanIndicator global={global} />
          </Box>
        </Tooltip>
      ) : !mature ? (
        <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Switch
              checked={therapist}
              onChange={onToggle}
              disabled={disabled}
              size="small"
            />
          </Box>

          <Box display="flex" alignItems="center" gap={0.75}>
            {isLoading && <CircularProgress size={14} />}
            {!isLoading && (
              <Tooltip
                title={`Effective for this therapist: ${effective ? 'ON' : 'OFF'}.`}
              >
                <Box>
                  <Semaphore
                    enabled={effective}
                    enabledText="Effective: on"
                    disabledText="Effective: off"
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      ) : null}

      {mature || !globalOnForced ? (
        <GlobalBooleanIndicator global={global} />
      ) : null}
    </Box>
  );
};

const EnumTherapistRow = ({
  entry,
  therapistId,
  therapistRecord,
  globalConfig,
  onAfterChange,
}: CommonProps & { entry: EnumFlagEntry }) => {
  const [updateTherapist, { isLoading }] = useUpdate('therapists', {
    id: therapistId,
  });

  const { global, therapist, effective } = computeEffectiveEnum(
    entry,
    globalConfig,
    therapistRecord,
  );

  const mature = entry.stability === 'mature';
  const selectValue = therapist ?? '__global__';

  const onChange = useCallback(
    async (nextValue: string) => {
      if (!entry.therapistField) return;
      await updateTherapist(
        'therapists',
        {
          id: therapistId,
          data: {
            [entry.therapistField]:
              nextValue === '__global__' ? null : nextValue,
            lastUpdatedAt: new Date(),
          },
        },
        {
          onSuccess: () => {
            onAfterChange?.();
          },
        },
      );
    },
    [entry.therapistField, onAfterChange, therapistId, updateTherapist],
  );

  return (
    <Box
      sx={{
        py: 1.25,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Header
        label={entry.label}
        description={entry.description}
        mature={mature}
      />

      {!mature ? (
        <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
          <FormControl size="small" disabled={isLoading} sx={{ minWidth: 220 }}>
            <InputLabel>Mode</InputLabel>
            <Select
              value={selectValue}
              label="Mode"
              onChange={event => {
                void onChange(event.target.value);
              }}
            >
              <MenuItem value="__global__">Use global mode</MenuItem>
              {entry.options.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary">
            Effective: <strong>{effective}</strong>
          </Typography>

          {isLoading && <CircularProgress size={14} />}
        </Box>
      ) : null}

      <GlobalEnumIndicator value={global} />
    </Box>
  );
};

export const FeatureFlagTherapistRow = (
  props: CommonProps & { entry: FlagEntry },
) => {
  if (props.entry.kind === 'boolean') {
    return (
      <BooleanTherapistRow {...props} entry={props.entry} />
    );
  }
  return <EnumTherapistRow {...props} entry={props.entry} />;
};
